"use client";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { ScrollToTop } from "@/components/ui/scroll-to-top";
import { useJsonTranslation } from "@/hooks/useJsonTranslation";
import {
  CheckCircle2,
  Clock,
  Cookie,
  Database,
  FileText,
  Gavel,
  Globe,
  Mail,
  Scale,
  Shield,
  UserCheck,
  Users,
} from "lucide-react";

const sections = [
  { id: "introduction", titleKey: "sections.introduction", icon: Shield },
  {
    id: "information-collected",
    titleKey: "sections.informationCollected",
    icon: Database,
  },
  { id: "how-we-use", titleKey: "sections.howWeUse", icon: UserCheck },
  { id: "data-sharing", titleKey: "sections.dataSharing", icon: Users },
  { id: "cookies", titleKey: "sections.cookies", icon: Cookie },
  { id: "data-retention", titleKey: "sections.dataRetention", icon: Clock },
  { id: "your-rights", titleKey: "sections.yourRights", icon: Gavel },
  { id: "gdpr", titleKey: "sections.gdpr", icon: Globe },
  { id: "contact", titleKey: "sections.contact", icon: Mail },
];

export default function PrivacyPolicyPage() {
  const { t } = useJsonTranslation("PrivacyPolicy");

  return (
    <div className="container mx-auto px-4 py-8 pt-24 sm:py-16">
      <div className="mx-auto max-w-4xl">
        {/* Header */}
        <div className="mb-12 text-center">
          <div className="bg-leaf-500/20 mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-2xl">
            <Shield className="text-leaf-600 dark:text-leaf-400 h-8 w-8" />
          </div>
          <h1 className="text-foreground mb-4 text-4xl font-bold">
            {t("title")}
          </h1>
          <div className="text-muted-foreground flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-sm">
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4" />
              <span>{t("lastUpdated")}</span>
            </div>
            <span>•</span>
            <span>{t("version")}</span>
          </div>
        </div>

        {/* Table of Contents */}
        <Card variant="glass" className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="text-leaf-600 dark:text-leaf-400 h-5 w-5" />
              {t("tableOfContents")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-2 sm:grid-cols-2">
              {sections.map((section, index) => {
                const Icon = section.icon;
                return (
                  <a
                    key={section.id}
                    href={`#${section.id}`}
                    className="group hover:bg-foreground/5 flex items-center gap-2 rounded-lg p-2 transition-colors"
                  >
                    <span className="text-leaf-600 dark:text-leaf-400 w-6 text-sm font-semibold">
                      {index + 1}.
                    </span>
                    <Icon className="text-muted-foreground group-hover:text-leaf-600 dark:group-hover:text-leaf-400 h-4 w-4 transition-colors" />
                    <span className="text-muted-foreground group-hover:text-foreground text-sm transition-colors">
                      {t(section.titleKey)}
                    </span>
                  </a>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Content */}
        <div className="space-y-8">
          {/* Introduction */}
          <Card id="introduction" variant="glass" className="scroll-mt-24">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="text-leaf-600 dark:text-leaf-400 h-5 w-5" />
                1. {t("sections.introduction")}
              </CardTitle>
            </CardHeader>
            <CardContent className="text-muted-foreground space-y-4">
              <p>{t("content.intro.p1")}</p>
              <div className="bg-leaf-500/10 border-leaf-500/30 rounded-lg border p-4">
                <p className="text-leaf-400 font-medium">
                  {t("content.intro.company")}
                </p>
              </div>
              <p>{t("content.intro.p2")}</p>
            </CardContent>
          </Card>

          {/* Information We Collect */}
          <Card
            id="information-collected"
            variant="glass"
            className="scroll-mt-24"
          >
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Database className="text-leaf-600 dark:text-leaf-400 h-5 w-5" />
                2. {t("sections.informationCollected")}
              </CardTitle>
            </CardHeader>
            <CardContent className="text-muted-foreground space-y-6">
              <div>
                <h4 className="text-foreground mb-2 font-semibold">
                  {t("content.accountInfo.title")}
                </h4>
                <ul className="space-y-2">
                  {[0, 1, 2].map((i) => (
                    <li key={i} className="flex items-start gap-2">
                      <CheckCircle2 className="text-leaf-400 mt-1 h-4 w-4 flex-shrink-0" />
                      <span>{t(`content.accountInfo.items.${i}`)}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <Separator />
              <div>
                <h4 className="text-foreground mb-2 font-semibold">
                  {t("content.usageData.title")}
                </h4>
                <ul className="space-y-2">
                  {[0, 1, 2].map((i) => (
                    <li key={i} className="flex items-start gap-2">
                      <CheckCircle2 className="text-leaf-400 mt-1 h-4 w-4 flex-shrink-0" />
                      <span>{t(`content.usageData.items.${i}`)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </CardContent>
          </Card>

          {/* How We Use */}
          <Card id="how-we-use" variant="glass" className="scroll-mt-24">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <UserCheck className="text-leaf-600 dark:text-leaf-400 h-5 w-5" />
                3. {t("sections.howWeUse")}
              </CardTitle>
            </CardHeader>
            <CardContent className="text-muted-foreground space-y-4">
              <p>{t("content.howWeUse.p")}</p>
              <ul className="space-y-2">
                {[0, 1, 2, 3, 4].map((i) => (
                  <li key={i} className="flex items-start gap-2">
                    <CheckCircle2 className="text-leaf-400 mt-1 h-4 w-4 flex-shrink-0" />
                    <span>{t(`content.howWeUse.items.${i}`)}</span>
                  </li>
                ))}
              </ul>
              <div className="bg-leaf-500/10 border-leaf-500/30 rounded-lg border p-4">
                <p className="text-leaf-600 dark:text-leaf-400">
                  {t("content.howWeUse.noSell")}
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Data Sharing */}
          <Card id="data-sharing" variant="glass" className="scroll-mt-24">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="text-leaf-600 dark:text-leaf-400 h-5 w-5" />
                4. {t("sections.dataSharing")}
              </CardTitle>
            </CardHeader>
            <CardContent className="text-muted-foreground space-y-4">
              <p>{t("content.dataSharing.p")}</p>
              <ul className="space-y-2">
                {[0, 1, 2].map((i) => (
                  <li key={i} className="flex items-start gap-2">
                    <CheckCircle2 className="text-leaf-400 mt-1 h-4 w-4 flex-shrink-0" />
                    <span>{t(`content.dataSharing.items.${i}`)}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>

          {/* Cookies */}
          <Card id="cookies" variant="glass" className="scroll-mt-24">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Cookie className="text-leaf-600 dark:text-leaf-400 h-5 w-5" />
                5. {t("sections.cookies")}
              </CardTitle>
            </CardHeader>
            <CardContent className="text-muted-foreground space-y-4">
              <p>{t("content.cookies.p")}</p>
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="bg-foreground/5 rounded-lg p-3">
                  <h5 className="text-foreground text-sm font-medium">
                    {t("content.cookies.essential")}
                  </h5>
                  <p className="mt-1 text-sm">
                    {t("content.cookies.essentialDesc")}
                  </p>
                </div>
                <div className="bg-foreground/5 rounded-lg p-3">
                  <h5 className="text-foreground text-sm font-medium">
                    {t("content.cookies.functional")}
                  </h5>
                  <p className="mt-1 text-sm">
                    {t("content.cookies.functionalDesc")}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Data Retention */}
          <Card id="data-retention" variant="glass" className="scroll-mt-24">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="text-leaf-600 dark:text-leaf-400 h-5 w-5" />
                6. {t("sections.dataRetention")}
              </CardTitle>
            </CardHeader>
            <CardContent className="text-muted-foreground space-y-4">
              <p>{t("content.dataRetention.p")}</p>
              <ul className="space-y-2">
                {[0, 1, 2].map((i) => (
                  <li key={i} className="flex items-start gap-2">
                    <Clock className="text-muted-foreground mt-1 h-4 w-4 flex-shrink-0" />
                    <span>{t(`content.dataRetention.items.${i}`)}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>

          {/* Your Rights */}
          <Card id="your-rights" variant="glass" className="scroll-mt-24">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Gavel className="text-leaf-600 dark:text-leaf-400 h-5 w-5" />
                7. {t("sections.yourRights")}
              </CardTitle>
            </CardHeader>
            <CardContent className="text-muted-foreground space-y-4">
              <p>{t("content.yourRights.p")}</p>
              <ul className="space-y-2">
                {[0, 1, 2, 3, 4].map((i) => (
                  <li key={i} className="flex items-start gap-2">
                    <CheckCircle2 className="text-leaf-400 mt-1 h-4 w-4 flex-shrink-0" />
                    <span>{t(`content.yourRights.items.${i}`)}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>

          {/* GDPR */}
          <Card id="gdpr" variant="glass" className="scroll-mt-24">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Globe className="text-leaf-600 dark:text-leaf-400 h-5 w-5" />
                8. {t("sections.gdpr")}
              </CardTitle>
            </CardHeader>
            <CardContent className="text-muted-foreground space-y-4">
              <p>{t("content.gdpr.p")}</p>
              <ul className="space-y-2">
                {[0, 1, 2].map((i) => (
                  <li key={i} className="flex items-start gap-2">
                    <Scale className="text-muted-foreground mt-1 h-4 w-4 flex-shrink-0" />
                    <span>{t(`content.gdpr.items.${i}`)}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>

          {/* Contact */}
          <Card id="contact" variant="glass" className="scroll-mt-24">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Mail className="text-leaf-600 dark:text-leaf-400 h-5 w-5" />
                9. {t("sections.contact")}
              </CardTitle>
            </CardHeader>
            <CardContent className="text-muted-foreground space-y-4">
              <p>{t("content.contact.p")}</p>
              <div className="bg-foreground/5 space-y-2 rounded-lg p-4">
                <p>
                  <strong className="text-foreground">
                    {t("content.contact.email")}{" "}
                  </strong>
                  <a
                    href="mailto:privacy@vidseeds.ai"
                    className="text-leaf-600 dark:text-leaf-400 hover:underline"
                  >
                    privacy@vidseeds.ai
                  </a>
                </p>
                <p>
                  <strong className="text-foreground">
                    {t("content.contact.responseTime")}{" "}
                  </strong>
                  {t("content.contact.responseValue")}
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Scroll to Top */}
      <ScrollToTop />
    </div>
  );
}
